/**
 * Budget API Service
 *
 * Provides methods for managing client and training budgets through the backend API.
 * All methods use tenant-aware headers and handle authentication automatically.
 * Includes comprehensive error handling with automatic logging and user-friendly error messages.
 *
 * @module lib/services/budgets
 */

import { get, put } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from './api-utils'
import { logNetworkError, logAuthError, logError } from '@/utils/error-logger'
import { getErrorMessageByStatusCode, getErrorActionByStatusCode } from '@/constants/error-messages'
import type {
  ClientBudgetSettings,
  UpdateClientBudgetRequest,
  TrainingBudget,
  UpdateTrainingBudgetRequest,
  ProjectionsSummary,
  ClientBudgetsList,
  ApiError,
} from '@/types/budgets'

const API_NAME = 'monumenta'

/**
 * Enhanced API error class with status code and user-friendly messages
 */
export class BudgetApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public userMessage?: string,
    public action?: string,
    public originalError?: unknown
  ) {
    super(message)
    this.name = 'BudgetApiError'
  }
}

/**
 * Extract status code from Amplify API error
 */
function getStatusCodeFromError(error: any): number | undefined {
  // Amplify API v6 error format
  if (error?.response?.statusCode) {
    return error.response.statusCode
  }
  // Alternative formats
  if (error?.statusCode) {
    return error.statusCode
  }
  if (error?.$metadata?.httpStatusCode) {
    return error.$metadata.httpStatusCode
  }
  return undefined
}

/**
 * Handle API errors with logging and user-friendly message transformation
 */
function handleApiError(
  error: unknown,
  context: {
    operation: string
    page: string
    clientCode?: string
    year?: number
  }
): never {
  const statusCode = getStatusCodeFromError(error)
  const errorMessage = error instanceof Error ? error.message : String(error)

  // Log error with context
  if (statusCode === 401) {
    logAuthError('authentication', error, {
      page: context.page,
      action: context.operation
    })
  } else if (statusCode === 403) {
    logAuthError('authorization', error, {
      page: context.page,
      action: context.operation
    })
  } else {
    logNetworkError(context.operation, error, {
      page: context.page,
      action: context.operation
    })
  }

  // Get user-friendly message
  const userMessage = getErrorMessageByStatusCode(
    statusCode || 500,
    'pt-BR',
    { year: context.year, clientCode: context.clientCode }
  )

  const action = statusCode ? getErrorActionByStatusCode(statusCode) : 'retry'

  // Throw enhanced error
  throw new BudgetApiError(
    errorMessage,
    statusCode,
    userMessage,
    action,
    error
  )
}

/**
 * Get client budget for a specific year
 *
 * @param clientCode - Client code (e.g., "000054")
 * @param year - Budget year (e.g., 2025)
 * @returns Client budget settings with metadata
 * @throws BudgetApiError if request fails or budget not found
 *
 * @example
 * ```typescript
 * try {
 *   const budget = await getClientBudget('000054', 2025)
 *   console.log(budget.settings.targetRevenue) // 16000000
 * } catch (error) {
 *   if (error instanceof BudgetApiError) {
 *     console.error(error.userMessage) // User-friendly message
 *   }
 * }
 * ```
 */
export async function getClientBudget(
  clientCode: string,
  year: number
): Promise<ClientBudgetSettings> {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: `/monumenta/budgets/client/${clientCode}`,
      options: {
        queryParams: { year: year.toString() },
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as ClientBudgetSettings
  } catch (error) {
    handleApiError(error, {
      operation: 'getClientBudget',
      page: 'ClientDashboard',
      clientCode,
      year
    })
  }
}

/**
 * Update client budget settings for a specific year
 *
 * Supports partial updates - only provide fields that should be changed.
 * All numeric values should be provided as plain numbers (not formatted strings).
 *
 * @param clientCode - Client code (e.g., "000054")
 * @param year - Budget year (e.g., 2025)
 * @param settings - Budget settings to update (partial)
 * @returns Updated client budget settings with metadata
 * @throws BudgetApiError if validation fails or request fails
 *
 * @example
 * ```typescript
 * try {
 *   const updated = await updateClientBudget('000054', 2025, {
 *     targetRevenue: 18000000,
 *     labourCost: 9000000
 *   })
 *   console.log(updated.metadata.lastUpdated) // "2025-10-22T15:45:00.000Z"
 * } catch (error) {
 *   if (error instanceof BudgetApiError && error.statusCode === 400) {
 *     // Handle validation error
 *   }
 * }
 * ```
 */
export async function updateClientBudget(
  clientCode: string,
  year: number,
  settings: Partial<UpdateClientBudgetRequest['settings']>
): Promise<ClientBudgetSettings> {
  try {
    const requestBody: UpdateClientBudgetRequest = { settings }

    const restOperation = put({
      apiName: API_NAME,
      path: `/monumenta/budgets/client/${clientCode}/${year}`,
      options: {
        body: JSON.parse(JSON.stringify(requestBody)),
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as ClientBudgetSettings
  } catch (error) {
    handleApiError(error, {
      operation: 'updateClientBudget',
      page: 'ClientDashboard',
      clientCode,
      year
    })
  }
}

/**
 * List all client budgets for a specific year
 *
 * @param year - Budget year (e.g., 2025)
 * @returns List of client budgets with summary metadata
 * @throws BudgetApiError if request fails
 *
 * @example
 * ```typescript
 * const list = await listClientBudgets(2025)
 * console.log(`Found ${list.totalClients} clients with budget data`)
 * ```
 */
export async function listClientBudgets(year: number): Promise<ClientBudgetsList> {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/monumenta/budgets/clients',
      options: {
        queryParams: { year: year.toString() },
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as ClientBudgetsList
  } catch (error) {
    handleApiError(error, {
      operation: 'listClientBudgets',
      page: 'ClientsList',
      year
    })
  }
}

/**
 * Get training budget for a specific year
 *
 * @param year - Budget year (e.g., 2025)
 * @returns Training budget with variance and utilization calculations
 * @throws BudgetApiError if request fails or budget not found
 *
 * @example
 * ```typescript
 * const training = await getTrainingBudget(2025)
 * console.log(`Utilization: ${training.utilizationPercent}%`)
 * ```
 */
export async function getTrainingBudget(year: number): Promise<TrainingBudget> {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/monumenta/budgets/training',
      options: {
        queryParams: { year: year.toString() },
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as TrainingBudget
  } catch (error) {
    handleApiError(error, {
      operation: 'getTrainingBudget',
      page: 'TrainingPage',
      year
    })
  }
}

/**
 * Update training budget for a specific year
 *
 * @param year - Budget year (e.g., 2025)
 * @param budget - Training budget to update (partial)
 * @returns Updated training budget with recalculated variance
 * @throws BudgetApiError if validation fails or request fails
 *
 * @example
 * ```typescript
 * const updated = await updateTrainingBudget(2025, {
 *   actualCost: 312000
 * })
 * console.log(updated.variance) // Automatically calculated
 * ```
 */
export async function updateTrainingBudget(
  year: number,
  budget: UpdateTrainingBudgetRequest
): Promise<TrainingBudget> {
  try {
    const restOperation = put({
      apiName: API_NAME,
      path: `/monumenta/budgets/training/${year}`,
      options: {
        body: JSON.parse(JSON.stringify(budget)),
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as TrainingBudget
  } catch (error) {
    handleApiError(error, {
      operation: 'updateTrainingBudget',
      page: 'TrainingPage',
      year
    })
  }
}

/**
 * Get company-wide budget projections summary for a specific year
 *
 * Aggregates all client budgets to provide company-wide totals.
 *
 * @param year - Budget year (e.g., 2025)
 * @returns Projections summary with aggregated totals
 * @throws BudgetApiError if request fails
 *
 * @example
 * ```typescript
 * const projections = await getProjectionsSummary(2025)
 * console.log(`Total Revenue: R$ ${projections.projections.totalRevenue}`)
 * ```
 */
export async function getProjectionsSummary(year: number): Promise<ProjectionsSummary> {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/monumenta/budgets/projections',
      options: {
        queryParams: { year: year.toString() },
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json() as unknown
    return data as ProjectionsSummary
  } catch (error) {
    handleApiError(error, {
      operation: 'getProjectionsSummary',
      page: 'ProjectionsPage',
      year
    })
  }
}
